import { db } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  limit 
} from 'firebase/firestore';
import { nanoid } from 'nanoid';

// Beta Invite Service
// Handles creation, validation, and management of beta invite codes
export const BetaInviteService = {
  // Check if beta mode is enabled
  isBetaModeEnabled: async () => {
    try {
      const settingsRef = doc(db, 'system_settings', 'beta_settings');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        return settingsDoc.data().betaModeEnabled === true;
      }
      return false; // Default to disabled if no settings exist
    } catch (error) {
      console.error('Error checking beta mode status:', error);
      return false; // Default to disabled on error
    }
  },

  // Toggle beta mode on/off
  toggleBetaMode: async (enabled, adminId) => {
    try {
      if (!adminId) {
        throw new Error('Admin ID is required to toggle beta mode');
      }
      
      const settingsRef = doc(db, 'system_settings', 'beta_settings');
      
      // Check if the document exists first
      const settingsDoc = await getDoc(settingsRef);
      
      // Create the document with all required fields if it doesn't exist
      if (!settingsDoc.exists()) {
        await setDoc(settingsRef, {
          betaModeEnabled: enabled,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: adminId,
          updatedBy: adminId
        });
      } else {
        // Update the existing document
        await updateDoc(settingsRef, {
          betaModeEnabled: enabled,
          updatedAt: new Date().toISOString(),
          updatedBy: adminId
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error toggling beta mode:', error);
      throw error;
    }
  },

  // Generate a new beta invite code
  generateBetaCode: async (adminId, description = '') => {
    try {
      // Generate a unique code
      const code = nanoid(10); // 10 character unique code
      
      // Create a new document in the betaInvites collection
      const betaInviteRef = doc(collection(db, 'betaInvites'));
      await setDoc(betaInviteRef, {
        code,
        description,
        isUsed: false,
        createdBy: adminId,
        createdAt: new Date().toISOString(),
        usedBy: null,
        usedAt: null
      });
      
      return { id: betaInviteRef.id, code };
    } catch (error) {
      console.error('Error generating beta code:', error);
      throw error;
    }
  },

  // Get all beta invite codes
  getAllBetaCodes: async () => {
    try {
      const betaInvitesRef = collection(db, 'betaInvites');
      const snapshot = await getDocs(betaInvitesRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting beta codes:', error);
      throw error;
    }
  },

  // Delete a beta invite code
  deleteBetaCode: async (codeId) => {
    try {
      await deleteDoc(doc(db, 'betaInvites', codeId));
      return true;
    } catch (error) {
      console.error('Error deleting beta code:', error);
      throw error;
    }
  },

  // Validate a beta code and mark it as used if valid
  validateAndUseBetaCode: async (code, userId, userEmail) => {
    try {
      // Check if beta mode is enabled
      const betaModeEnabled = await BetaInviteService.isBetaModeEnabled();
      if (!betaModeEnabled) {
        return { valid: false, message: 'Beta mode is not currently active' };
      }
      
      // Query for the beta code
      const betaInvitesRef = collection(db, 'betaInvites');
      const q = query(betaInvitesRef, where('code', '==', code), limit(1));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return { valid: false, message: 'Invalid beta code' };
      }
      
      const betaInviteDoc = snapshot.docs[0];
      const betaInviteData = betaInviteDoc.data();
      
      // Check if the code has already been used
      if (betaInviteData.isUsed) {
        return { valid: false, message: 'This beta code has already been used' };
      }
      
      // Mark the code as used
      await updateDoc(doc(db, 'betaInvites', betaInviteDoc.id), {
        isUsed: true,
        usedBy: userId,
        userEmail: userEmail, // Store the user's email
        usedAt: new Date().toISOString()
      });
      
      return { valid: true, message: 'Beta code accepted' };
    } catch (error) {
      console.error('Error validating beta code:', error);
      return { valid: false, message: 'Error validating beta code' };
    }
  },

  // Get public beta status (for registration page)
  getPublicBetaStatus: async () => {
    try {
      const betaModeEnabled = await BetaInviteService.isBetaModeEnabled();
      return { betaModeEnabled };
    } catch (error) {
      console.error('Error getting public beta status:', error);
      return { betaModeEnabled: false }; // Default to disabled on error
    }
  }
};
